import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { eachDayOfInterval, format, isWeekend, parseISO } from 'date-fns';

import Alert from '@mui/material/Alert';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Select from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import SearchIcon from '@mui/icons-material/Search';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import DatePickerField from '../../../sharedModules/form/components/DatePickerField';
import PageHeader from '../../../sharedModules/layout/PageHeader';
import OrderStatusChip from '../../components/OrderStatusChip';
import WorkTypeChip from '../../components/WorkTypeChip';
import useProjectsData from '../../hooks/useProjectsData';
import { CREW_ASSIGNMENTS, PROJECT_JOBS, PROJECT_ORDERS, ProjectOrder, TECHNICIANS } from '../../data/projectsData';
import {
  TODAY,
  assignmentsForOrder,
  jobById,
  ordersForJob,
  scheduledHours,
  workTypeLabel,
} from '../../utils/projectsUtils';

const START_TIMES = ['06:00', '06:30', '07:00', '07:30', '08:00', '09:00', '10:00', '12:00', '13:00'];

interface DraftAssignment {
  order_no: string;
  emp_id: string;
  tech_name: string;
  date: string;
  start_time: string;
  duration: number;
}

type OrderOption = ProjectOrder & { jobLabel: string };

// Search orders by number, job ID, customer, work type, or status; cap the list so the
// 700+ orders stay snappy in the dropdown.
const orderFilter = createFilterOptions<OrderOption>({
  limit: 40,
  stringify: (option) => `${option.order_no} ${option.jobLabel} ${option.job_id} ${option.work_type} ${option.status}`,
});

const conflictFor = (empId: string, date: string, orderNo: string): string | null => {
  const existing = CREW_ASSIGNMENTS.find(
    (assignment) => assignment.emp_id === empId && assignment.date === date && assignment.order_no !== orderNo,
  );
  return existing ? `Already on #${existing.order_no} (${existing.start_time}, ${existing.duration}h)` : null;
};

interface ScheduleRow {
  date: string;
  emp_id: string;
  tech_name: string;
  start_time: string;
  duration: number;
  kind: 'existing' | 'draft';
  draftIndex?: number;
  conflict?: string | null;
}

const ProjectSchedulingPage: React.FunctionComponent = () => {
  const location = useLocation();
  const { source, loading, error } = useProjectsData();
  const preselectedJob = new URLSearchParams(location.search).get('job') || '';

  const [jobId, setJobId] = useState<string>(preselectedJob);
  const [orderNo, setOrderNo] = useState<string>('');
  const [selectedTechs, setSelectedTechs] = useState<string[]>([]);
  const [startDate, setStartDate] = useState<string>(TODAY);
  const [endDate, setEndDate] = useState<string>(TODAY);
  const [startTime, setStartTime] = useState<string>('07:00');
  const [hoursPerDay, setHoursPerDay] = useState<number>(8);
  const [skipWeekends, setSkipWeekends] = useState<boolean>(true);
  const [draft, setDraft] = useState<DraftAssignment[]>([]);

  // Once live data has loaded, clear the selection only if the chosen job genuinely doesn't
  // exist. Guarding on source === 'live' avoids nuking a deep-linked ?job= during the brief
  // fallback phase before the live dataset arrives.
  useEffect(() => {
    if (source === 'live' && jobId && !PROJECT_JOBS.some((job) => job.job_id === jobId)) {
      setJobId('');
      setOrderNo('');
      setDraft([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source]);

  const orders = ordersForJob(jobId);
  const selectedOrder = orders.find((order) => order.order_no === orderNo);
  const job = selectedOrder ? jobById(selectedOrder.job_id) : undefined;
  const existingAssignments = useMemo(() => (orderNo ? assignmentsForOrder(orderNo) : []), [orderNo]);

  const orderOptions = useMemo<OrderOption[]>(
    () =>
      PROJECT_ORDERS.map((order) => {
        const owningJob = jobById(order.job_id);
        return { ...order, jobLabel: owningJob?.company || owningJob?.description || order.job_id };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [source],
  );

  const chooseOrder = (newOrderNo: string, newJobId?: string) => {
    if (newJobId) setJobId(newJobId);
    setOrderNo(newOrderNo);
    setSelectedTechs([]);
    setDraft([]);
  };

  const eligibleDayCount = useMemo(() => {
    if (!startDate || !endDate || startDate > endDate) return 0;
    return eachDayOfInterval({ start: parseISO(startDate), end: parseISO(endDate) }).filter(
      (day) => !skipWeekends || !isWeekend(day),
    ).length;
  }, [startDate, endDate, skipWeekends]);

  const previewCount = selectedTechs.length * eligibleDayCount;

  const addToDraft = () => {
    if (!orderNo || selectedTechs.length === 0 || !startDate || !endDate || startDate > endDate) return;
    const days = eachDayOfInterval({ start: parseISO(startDate), end: parseISO(endDate) }).filter(
      (day) => !skipWeekends || !isWeekend(day),
    );
    const additions: DraftAssignment[] = [];
    days.forEach((day) => {
      const isoDate = format(day, 'yyyy-MM-dd');
      selectedTechs.forEach((empId) => {
        const tech = TECHNICIANS.find((technician) => technician.emp_id === empId);
        const alreadyDrafted = draft.some((entry) => entry.emp_id === empId && entry.date === isoDate);
        if (tech && !alreadyDrafted) {
          additions.push({
            order_no: orderNo,
            emp_id: empId,
            tech_name: tech.name,
            date: isoDate,
            start_time: startTime,
            duration: hoursPerDay,
          });
        }
      });
    });
    setDraft((prev) =>
      [...prev, ...additions].sort((a, b) => a.date.localeCompare(b.date) || a.tech_name.localeCompare(b.tech_name)),
    );
  };

  const removeDraft = (index: number) => setDraft((prev) => prev.filter((_, i) => i !== index));

  // Unified schedule: existing assignments + draft additions, merged and grouped by date so
  // the planner sees the real resulting day-by-day crew instead of two separate tables.
  const scheduleByDate = useMemo<Array<[string, ScheduleRow[]]>>(() => {
    const rows: ScheduleRow[] = [
      ...existingAssignments.map((assignment) => ({
        date: assignment.date,
        emp_id: assignment.emp_id,
        tech_name: assignment.tech_name,
        start_time: assignment.start_time,
        duration: assignment.duration,
        kind: 'existing' as const,
      })),
      ...draft.map((entry, index) => ({
        date: entry.date,
        emp_id: entry.emp_id,
        tech_name: entry.tech_name,
        start_time: entry.start_time,
        duration: entry.duration,
        kind: 'draft' as const,
        draftIndex: index,
        conflict: conflictFor(entry.emp_id, entry.date, entry.order_no),
      })),
    ].sort(
      (a, b) =>
        a.date.localeCompare(b.date) ||
        (a.kind === b.kind ? 0 : a.kind === 'existing' ? -1 : 1) ||
        a.tech_name.localeCompare(b.tech_name),
    );
    const groups = new Map<string, ScheduleRow[]>();
    rows.forEach((row) => {
      if (!groups.has(row.date)) groups.set(row.date, []);
      groups.get(row.date)!.push(row);
    });
    return Array.from(groups.entries());
  }, [existingAssignments, draft]);

  const existingHrs = scheduledHours(existingAssignments);
  const draftHrs = draft.reduce((total, entry) => total + entry.duration, 0);
  const conflictCount = draft.filter((entry) => conflictFor(entry.emp_id, entry.date, entry.order_no)).length;

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        mainTitle="Crew Scheduling"
        subTitle="Assign technicians to a project order"
        titleIcon={<EditCalendarIcon />}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Couldn’t load project data from ServMan. Refresh to try again.
        </Alert>
      )}
      {loading && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Loading project data…
        </Typography>
      )}

      {/* Step 1 — choose an order */}
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="overline" color="text.secondary">
            Step 1 · Choose an order
          </Typography>
          <Autocomplete
            sx={{ maxWidth: 620, mt: 0.5 }}
            size="small"
            options={orderOptions}
            value={orderOptions.find((option) => option.order_no === orderNo) || null}
            onChange={(_event, value) => (value ? chooseOrder(value.order_no, value.job_id) : setOrderNo(''))}
            filterOptions={(options, state) => (state.inputValue ? orderFilter(options, state) : [])}
            getOptionLabel={(option) => `#${option.order_no} — ${option.jobLabel}`}
            isOptionEqualToValue={(option, value) => option.order_no === value.order_no}
            noOptionsText="Type an order number, job ID, or customer"
            renderOption={(props, option) => (
              <li {...props} key={option.order_no}>
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    #{option.order_no} · {workTypeLabel(option.work_type)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.jobLabel} · {option.job_id} · {option.status || 'No status'}
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search for an order — number, job, or customer"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <SearchIcon fontSize="small" sx={{ color: 'text.disabled', mx: 0.5 }} />
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          {selectedOrder && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Typography variant="h6" fontWeight={700}>
                  #{selectedOrder.order_no}
                </Typography>
                <WorkTypeChip workType={selectedOrder.work_type} />
                <OrderStatusChip status={selectedOrder.status} />
                {selectedOrder.unscheduled && (
                  <Chip label="Unscheduled" size="small" color="warning" variant="outlined" />
                )}
                <Box sx={{ flexGrow: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  {existingAssignments.length} assignment{existingAssignments.length === 1 ? '' : 's'} ·{' '}
                  {existingHrs.toLocaleString()} hrs scheduled to date
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {job?.company || job?.description} · {selectedOrder.job_id}
                {job?.project_coordinator && job.project_coordinator !== '*None*'
                  ? ` · Coordinator ${job.project_coordinator}`
                  : ''}
              </Typography>
            </Box>
          )}

          {orders.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 2, flexWrap: 'wrap' }}>
              <Typography variant="caption" color="text.secondary">
                {selectedOrder ? 'Orders on this job:' : 'Pick an order on this job:'}
              </Typography>
              {orders.map((order) => (
                <Tooltip
                  key={order.order_no}
                  title={`${workTypeLabel(order.work_type)} · ${order.status || 'No status'}`}
                >
                  <Chip
                    size="small"
                    label={`#${order.order_no}`}
                    variant={order.order_no === orderNo ? 'filled' : 'outlined'}
                    color={order.order_no === orderNo ? 'primary' : 'default'}
                    onClick={() => chooseOrder(order.order_no)}
                  />
                </Tooltip>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      {!selectedOrder && (
        <Card variant="outlined">
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="body1" color="text.secondary">
              {orders.length > 0
                ? 'Pick an order above to start scheduling its crew.'
                : 'Search for an order above to start building its crew schedule.'}
            </Typography>
          </CardContent>
        </Card>
      )}

      {selectedOrder && (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
          {/* Step 2 — add a crew block */}
          <Card variant="outlined" sx={{ width: { xs: '100%', md: 380 }, flexShrink: 0 }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Step 2 · Add a crew block
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Technicians</InputLabel>
                  <Select
                    multiple
                    value={selectedTechs}
                    onChange={(event) => setSelectedTechs(event.target.value as string[])}
                    input={<OutlinedInput label="Technicians" />}
                    renderValue={(selected) =>
                      (selected as string[])
                        .map((empId) => TECHNICIANS.find((t) => t.emp_id === empId)?.name)
                        .join(', ')
                    }
                  >
                    {TECHNICIANS.map((tech) => (
                      <MenuItem key={tech.emp_id} value={tech.emp_id}>
                        <Checkbox checked={selectedTechs.includes(tech.emp_id)} size="small" />
                        <ListItemText primary={tech.name} secondary={tech.emp_id} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <DatePickerField
                      label="Start"
                      value={startDate}
                      onChange={setStartDate}
                      variant="outlined"
                      fullWidth
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <DatePickerField label="End" value={endDate} onChange={setEndDate} variant="outlined" fullWidth />
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Start time</InputLabel>
                    <Select value={startTime} label="Start time" onChange={(event) => setStartTime(event.target.value)}>
                      {START_TIMES.map((time) => (
                        <MenuItem key={time} value={time}>
                          {time}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    label="Hrs/day"
                    type="number"
                    size="small"
                    fullWidth
                    value={hoursPerDay}
                    onChange={(event) => setHoursPerDay(Math.max(0.5, Math.min(12, Number(event.target.value))))}
                    inputProps={{ step: 0.5, min: 0.5, max: 12 }}
                  />
                </Box>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={skipWeekends}
                      onChange={(event) => setSkipWeekends(event.target.checked)}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">Skip weekends</Typography>}
                />

                <Button variant="contained" onClick={addToDraft} disabled={previewCount === 0}>
                  {previewCount > 0
                    ? `Add ${previewCount} assignment${previewCount === 1 ? '' : 's'}`
                    : 'Add to schedule'}
                </Button>
                {selectedTechs.length > 0 && eligibleDayCount > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                    {selectedTechs.length} tech{selectedTechs.length === 1 ? '' : 's'} × {eligibleDayCount} day
                    {eligibleDayCount === 1 ? '' : 's'} × {hoursPerDay} hrs
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Step 3 — review the resulting schedule */}
          <Card variant="outlined" sx={{ flexGrow: 1, width: '100%' }}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  flexWrap: 'wrap',
                }}
              >
                <Box>
                  <Typography variant="overline" color="text.secondary">
                    Step 3 · Review schedule for #{selectedOrder.order_no}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {existingAssignments.length} scheduled · {draft.length} new ·{' '}
                    {(existingHrs + draftHrs).toLocaleString()} total hrs
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="small" disabled={draft.length === 0} onClick={() => setDraft([])}>
                    Reset draft
                  </Button>
                  <Tooltip title="Writing schedules back to ServMan (ORDEMPS/SCHEDULE) isn’t available yet">
                    <span>
                      <Button size="small" variant="contained" color="success" disabled>
                        Submit {draft.length > 0 ? `${draft.length} ` : ''}to ServMan
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              </Box>

              {conflictCount > 0 && (
                <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mt: 1.5 }}>
                  {conflictCount} new assignment{conflictCount === 1 ? '' : 's'} conflict with the tech’s existing
                  bookings on other orders.
                </Alert>
              )}

              <Box
                sx={{ mt: 1.5, maxHeight: 540, overflowY: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}
              >
                {scheduleByDate.length === 0 ? (
                  <Box sx={{ py: 5, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      No crew scheduled yet. Use “Add a crew block” to build the schedule.
                    </Typography>
                  </Box>
                ) : (
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Technician</TableCell>
                        <TableCell>Start</TableCell>
                        <TableCell align="right">Hrs</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell padding="checkbox" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {scheduleByDate.map(([date, rows]) => {
                        const dayHrs = rows.reduce((sum, row) => sum + row.duration, 0);
                        return (
                          <React.Fragment key={date}>
                            <TableRow>
                              <TableCell colSpan={5} sx={{ backgroundColor: 'grey.100', py: 0.5 }}>
                                <Typography variant="caption" fontWeight={700}>
                                  {format(parseISO(date), 'EEE, MMM d')} · {rows.length} on crew · {dayHrs}h
                                </Typography>
                              </TableCell>
                            </TableRow>
                            {rows.map((row, index) => (
                              <TableRow
                                key={`${date}-${row.kind}-${row.emp_id}-${index}`}
                                hover
                                sx={{
                                  backgroundColor: row.conflict
                                    ? '#fff8e1'
                                    : row.kind === 'draft'
                                      ? 'primary.50'
                                      : 'inherit',
                                }}
                              >
                                <TableCell>{row.tech_name}</TableCell>
                                <TableCell>{row.start_time}</TableCell>
                                <TableCell align="right">{row.duration}</TableCell>
                                <TableCell>
                                  {row.kind === 'draft' ? (
                                    row.conflict ? (
                                      <Tooltip title={row.conflict}>
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            color: 'warning.main',
                                          }}
                                        >
                                          <WarningAmberIcon fontSize="small" />
                                          <Typography variant="caption">Conflict</Typography>
                                        </Box>
                                      </Tooltip>
                                    ) : (
                                      <Chip label="New" size="small" color="primary" variant="outlined" />
                                    )
                                  ) : (
                                    <Typography variant="caption" color="text.secondary">
                                      Scheduled
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell padding="checkbox">
                                  {row.kind === 'draft' && row.draftIndex !== undefined && (
                                    <IconButton size="small" onClick={() => removeDraft(row.draftIndex!)}>
                                      <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
};

export default ProjectSchedulingPage;
